/**
 * Offline-Warteschlange für SOS (RISIKEN §9, entschieden 2026-09-08).
 *
 * ## Das Problem
 * Der häufigste echte Notfall — Sturz im Wald, Unfall im Funkloch — ist genau
 * der, in dem kein Netz da ist. Eine SOS-Funktion, die dann stumm scheitert,
 * ist schlimmer als keine: Man verlässt sich darauf.
 *
 * ## Der Weg
 * Der Alarm wird **zuerst lokal abgelegt**, dann gesendet. Klappt das Senden
 * nicht, bleibt er liegen und der Service Worker versucht es per Background
 * Sync erneut, sobald wieder Netz da ist. Der Auslösezeitpunkt reist mit —
 * beim Server kommt der Alarm mit der Zeit an, zu der er ausgelöst wurde, nicht
 * mit der Zustellzeit.
 *
 * ## Was das nicht kann
 * Kein Netz, kein Anruf möglich → die App kann nichts tun. Deshalb steht im
 * SOS-Bildschirm immer die Notrufnummer als `tel:`-Link: Ein Notruf geht auch
 * über Netze, die keinen Datenverkehr durchlassen, und sogar im Fremdnetz.
 * Im Funkloch ist er der zuverlässigere Weg — nicht diese App.
 */

const DB_NAME = 'fh-sos'
const STORE = 'wartend'
export const SYNC_TAG = 'fh-sos-senden'

export interface WartenderAlarm {
  id: string
  kind: 'real' | 'test'
  triggeredAt: string
  shareLocation: boolean
  csrf: string
}

function db(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const anfrage = indexedDB.open(DB_NAME, 1)
    anfrage.onupgradeneeded = () => {
      anfrage.result.createObjectStore(STORE, { keyPath: 'id' })
    }
    anfrage.onsuccess = () => resolve(anfrage.result)
    anfrage.onerror = () => reject(anfrage.error ?? new Error('IndexedDB nicht verfügbar'))
  })
}

async function schreiben(alarm: WartenderAlarm): Promise<void> {
  const verbindung = await db()
  await new Promise<void>((resolve, reject) => {
    const t = verbindung.transaction(STORE, 'readwrite')
    t.objectStore(STORE).put(alarm)
    t.oncomplete = () => resolve()
    t.onerror = () => reject(t.error ?? new Error('Konnte nicht speichern'))
  })
  verbindung.close()
}

export async function entfernen(id: string): Promise<void> {
  const verbindung = await db()
  await new Promise<void>((resolve) => {
    const t = verbindung.transaction(STORE, 'readwrite')
    t.objectStore(STORE).delete(id)
    t.oncomplete = () => resolve()
    t.onerror = () => resolve()
  })
  verbindung.close()
}

export async function wartende(): Promise<WartenderAlarm[]> {
  const verbindung = await db()
  const alle = await new Promise<WartenderAlarm[]>((resolve) => {
    const t = verbindung.transaction(STORE, 'readonly')
    const anfrage = t.objectStore(STORE).getAll()
    anfrage.onsuccess = () => resolve(anfrage.result as WartenderAlarm[])
    anfrage.onerror = () => resolve([])
  })
  verbindung.close()
  return alle
}

export interface SendeErgebnis {
  /** `true`, wenn der Server den Alarm angenommen hat. */
  zugestellt: boolean
  /** Die Alarm-ID des Servers — nur bei Zustellung. */
  sosId?: string
  /** Wartet der Alarm auf Netz? */
  wartet: boolean
}

/**
 * Alarm auslösen — mit Netz sofort, ohne Netz später.
 *
 * Die Reihenfolge ist entscheidend: **erst ablegen, dann senden.** Umgekehrt
 * ginge ein Alarm verloren, dessen Übertragung mitten im Versand abbricht —
 * und das ist genau der Fall im Funkloch.
 */
export async function alarmSenden(input: {
  kind: 'real' | 'test'
  shareLocation: boolean
  csrf: string
}): Promise<SendeErgebnis> {
  const alarm: WartenderAlarm = {
    id: crypto.randomUUID(),
    kind: input.kind,
    triggeredAt: new Date().toISOString(),
    shareLocation: input.shareLocation,
    csrf: input.csrf,
  }

  // Ablegen darf nie den Alarm verhindern. Kann der Browser kein IndexedDB
  // (privates Fenster, Speicher voll), wird trotzdem gesendet — dann eben
  // ohne Nachreichung.
  await schreiben(alarm).catch(() => undefined)

  try {
    const res = await fetch('/api/v1/sos', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-fh-csrf': input.csrf },
      credentials: 'same-origin',
      body: JSON.stringify({
        kind: alarm.kind,
        triggeredAt: alarm.triggeredAt,
        shareLocation: alarm.shareLocation,
      }),
    })

    if (res.ok) {
      const daten = await res.json() as { id: string }
      await entfernen(alarm.id).catch(() => undefined)
      return { zugestellt: true, sosId: daten.id, wartet: false }
    }
  } catch {
    // Netzfehler — der Alarm bleibt liegen.
  }

  await syncAnmelden()
  return { zugestellt: false, wartet: true }
}

/**
 * Beim Service Worker anmelden, dass etwas zu senden ist.
 *
 * Background Sync gibt es nicht überall (Firefox und Safari kennen es nicht).
 * Wo es fehlt, greift der Fallback in `App.tsx`: erneut versuchen, sobald der
 * Browser wieder online meldet. Das ist schwächer — es setzt voraus, dass die
 * App offen ist —, aber besser als nichts, und es wird dem Nutzer gesagt.
 */
async function syncAnmelden(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.ready
    const sync = (reg as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> }
    }).sync
    if (sync) await sync.register(SYNC_TAG)
  } catch {
    // Kein Service Worker, kein Background Sync — der Fallback greift.
  }
}

/** Unterstützt dieser Browser echtes Nachreichen im Hintergrund? */
export function hatBackgroundSync(): boolean {
  return typeof ServiceWorkerRegistration !== 'undefined'
    && 'sync' in ServiceWorkerRegistration.prototype
}

/**
 * Wartende Alarme erneut versuchen — der Fallback ohne Background Sync.
 *
 * Wird aufgerufen, sobald der Browser wieder online meldet.
 */
export async function nachreichen(): Promise<number> {
  const offen = await wartende().catch(() => [])
  let erfolg = 0

  for (const alarm of offen) {
    try {
      const res = await fetch('/api/v1/sos', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-fh-csrf': alarm.csrf },
        credentials: 'same-origin',
        body: JSON.stringify({
          kind: alarm.kind,
          // Der ursprüngliche Zeitpunkt. Ohne ihn erschiene ein halbstündiger
          // Notfall als gerade eben passiert.
          triggeredAt: alarm.triggeredAt,
          shareLocation: alarm.shareLocation,
        }),
      })
      if (res.ok) {
        await entfernen(alarm.id)
        erfolg++
      }
    } catch {
      // Weiter offline. Beim nächsten Mal.
    }
  }
  return erfolg
}
